import React from 'react';
import { Code, Flex, Link, Tooltip } from '@radix-ui/themes';
import HashShortener from '../HashShortener';
import { RiskIndicator } from '../RiskIndicator';
import CopyButton from '../CopyButton';
import type { AddressDisplayFragment } from './AddressDisplay.generated';

interface IAddressDisplayProps {
  address: AddressDisplayFragment;
  isHighlighted?: boolean;
  showEntityName?: boolean;
}

/**
 * Component for displaying blockchain addresses with risk icons
 * When entity name is available and showEntityName is true, displays the entity name
 * with the full hash as tooltip. Otherwise displays shortened hash.
 * Automatically generates link to address detail page
 */
export function AddressDisplay({
  address,
  isHighlighted,
  showEntityName = true,
}: IAddressDisplayProps) {
  const entityName = address.entity?.name;
  const shouldShowEntityName = showEntityName && entityName;

  return (
    <Flex align="center" gap="2">
      {shouldShowEntityName ? (
        <>
          <Tooltip content={address.address}>
            <Link href={`/addresses/${address.address}`}>
              <Code variant={isHighlighted ? 'outline' : 'ghost'}>
                {entityName}
              </Code>
            </Link>
          </Tooltip>
          <CopyButton value={address.address} />
        </>
      ) : (
        <HashShortener
          hash={address.address}
          link={`/addresses/${address.address}`}
          isHighlighted={isHighlighted}
        />
      )}
      <RiskIndicator risks={address.risks} />
    </Flex>
  );
}
